import React from 'react';
import {
  Box,
  HStack,
  IconButton,
  Skeleton,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import Slider from 'react-slick';
import { AngleLeftIcon, AngleRightIcon, PromoIcon } from 'static/icons';
import { DiscountInfo } from 'types';
import useAxios from 'axios-hooks';
import styled from '@emotion/styled';
import { DetailsDiscount, ModalPage } from '.';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Banner = styled.div`
  background-color: ${({ theme }) => theme.colors.promoBg};
  padding: 24px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
`;

const IconButtonStyled = styled(IconButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.promoControls};
  padding: 8px;
  margin: 0 8px;
`;

const PromoIconStyled = styled(PromoIcon)`
  color: ${({ theme }) => theme.colors.promoControls};
`;

interface DiscountsProps {
  widgetId: string;
  divisionId: string | null;
  loadDiscountsManually: boolean;
}

export const Discounts: React.FC<DiscountsProps> = ({
  widgetId,
  divisionId,
  loadDiscountsManually,
}) => {
  const [{ data, loading, error }] = useAxios<DiscountInfo[]>(
    {
      url: `/booking-widget/client/widget/${widgetId}/discount`,
      params: {
        ...(divisionId ? { divisionId } : {}),
      },
    },
    { manual: loadDiscountsManually },
  );

  return !error && (!data || data?.length) ? (
    <Skeleton height="98px" isLoaded={!loading}>
      {data?.length ? (
        <DiscountSlider widgetId={widgetId} discounts={data} />
      ) : null}
    </Skeleton>
  ) : null;
};

interface DiscountSliderProps {
  widgetId: string;
  discounts: DiscountInfo[];
}

const DiscountSlider: React.FC<DiscountSliderProps> = ({
  widgetId,
  discounts,
}) => {
  const [isMobileDevice] = useMediaQuery('(max-width: 768px)');
  const [item, setItem] = React.useState<DiscountInfo | null>(null);
  const handleShowDetails = (discount: DiscountInfo) => () => setItem(discount);
  const resetItem = () => setItem(null);
  const settings = {
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
    dots: discounts.length <= 10,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    swipe: isMobileDevice,
  };
  const [slider, setSlider] = React.useState<Slider | null>(null);
  const isShowSliderButtons = !isMobileDevice && discounts.length > 1;

  return (
    <>
      <ModalPage isOpen={!!item} title="Aкции" onClose={resetItem}>
        {item && (
          <DetailsDiscount
            discount={item}
            widgetId={widgetId}
            discounts={discounts}
          />
        )}
      </ModalPage>

      <Box mb={10} position="relative">
        {isShowSliderButtons && (
          <>
            <IconButtonStyled
              aria-label="left-arrow"
              variant="link"
              position="absolute"
              left={1}
              top="50%"
              transform="translate(0%, -50%)"
              zIndex={2}
              onClick={() => slider?.slickPrev()}
            >
              <AngleLeftIcon />
            </IconButtonStyled>
            <IconButtonStyled
              aria-label="right-arrow"
              variant="link"
              position="absolute"
              right={1}
              top="50%"
              transform="translate(0%, -50%)"
              zIndex={2}
              onClick={() => slider?.slickNext()}
            >
              <AngleRightIcon />
            </IconButtonStyled>
          </>
        )}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Slider {...settings} ref={(ref) => setSlider(ref)}>
          {discounts.map((discount) => (
            <Box px={1} key={discount.id}>
              <Banner onClick={handleShowDetails(discount)}>
                <HStack
                  justify="center"
                  padding={isShowSliderButtons ? '0 40px' : undefined}
                >
                  <Box flexShrink={0}>
                    <PromoIconStyled />
                  </Box>
                  <Text color="graphite.1" noOfLines={1}>
                    {discount.overrideName}
                  </Text>
                </HStack>
              </Banner>
            </Box>
          ))}
        </Slider>
      </Box>
    </>
  );
};
